import { create } from 'zustand';
import type { PaymentType } from '@/lib/types';
import { MAX_AMOUNT } from '@/lib/constants';
import { useCartStore } from '@/store/cart-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PaymentState {
  /** Raw keypad input as a string (French comma as decimal separator). */
  currentAmount: string;
  selectedType: PaymentType | null;

  appendDigit: (digit: string) => void;
  addDecimal: () => void;
  backspace: () => void;
  clearAmount: () => void;
  selectType: (type: PaymentType) => void;
  setExactAmount: () => void;
  reset: () => void;
  getAmountAsNumber: () => number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse the comma-separated French input string into a float.
 */
function parseCommaAmount(input: string): number {
  if (!input || input === '') return 0;
  return parseFloat(input.replace(',', '.')) || 0;
}

// ---------------------------------------------------------------------------
// Store (runtime only — NOT persisted)
// ---------------------------------------------------------------------------

export const usePaymentStore = create<PaymentState>()((set, get) => ({
  currentAmount: '',
  selectedType: null,

  appendDigit: (digit) => {
    set((state) => {
      const current = state.currentAmount;

      // Enforce max 2 decimal places
      if (current.includes(',')) {
        const parts = current.split(',');
        if (parts[1].length >= 2) return state;
      }

      const candidate = current + digit;
      const numericValue = parseCommaAmount(candidate);

      // Enforce max amount
      if (numericValue > MAX_AMOUNT) return state;

      return { currentAmount: candidate };
    });
  },

  addDecimal: () => {
    set((state) => {
      if (state.currentAmount.includes(',')) return state;

      const next =
        state.currentAmount === '' ? '0,' : state.currentAmount + ',';
      return { currentAmount: next };
    });
  },

  backspace: () => {
    set((state) => {
      if (state.currentAmount.length === 0) return state;
      return { currentAmount: state.currentAmount.slice(0, -1) };
    });
  },

  clearAmount: () => set({ currentAmount: '' }),

  selectType: (type) => set({ selectedType: type }),

  setExactAmount: () => {
    // Read remaining amount from cart store
    const remaining: number = useCartStore.getState().getRemainingAmount();

    // Format as French comma string (e.g. 12,50)
    const formatted = remaining.toFixed(2).replace('.', ',');
    set({ currentAmount: formatted });
  },

  reset: () => set({ currentAmount: '', selectedType: null }),

  getAmountAsNumber: () => {
    return parseCommaAmount(get().currentAmount);
  },
}));
